import { useEffect, useState, useMemo } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { fetchAtaCategorias, fetchAtas, type Ata, type Categoria } from '@/services/catalog';

type ViewMode = 'lista' | 'categorizado';

const esferaOptions = ['Federal', 'Estadual', 'Municipal'] as const;

const isVisibleAta = (visibilidade: string) =>
  ['Visível', 'VisÃ­vel', 'Visivel'].includes(visibilidade);

export default function AtasPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [orgaoFilter, setOrgaoFilter] = useState('');
  const [uasgFilter, setUasgFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [esferaFilter, setEsferaFilter] = useState<'todas' | 'Federal' | 'Estadual' | 'Municipal'>('todas');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'todas' | 'Ativa' | 'Encerrada'>('todas');
  const [viewMode, setViewMode] = useState<ViewMode>('lista');
  const [expandedAta, setExpandedAta] = useState<number | null>(null);
  const [atas, setAtas] = useState<Ata[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);

  useEffect(() => {
    Promise.all([fetchAtas(), fetchAtaCategorias()]).then(([atasData, categoriasData]) => {
      setAtas(atasData);
      setCategorias(categoriasData);
    });
  }, []);

  const publicAtas = useMemo(() => atas.filter((a) => isVisibleAta(a.visibilidade)), [atas]);

  const orgaos = useMemo(() => {
    const set = new Set(publicAtas.map((a) => a.orgao));
    return Array.from(set).sort();
  }, [publicAtas]);

  const estados = useMemo(() => {
    const set = new Set(publicAtas.map((a) => a.estado).filter(Boolean));
    return Array.from(set).sort();
  }, [publicAtas]);

  const getCategoriaNomes = (ids: number[]) =>
    ids.map((id) => categorias.find((c) => c.id === id)?.nome || '').filter(Boolean);

  const filteredAtas = useMemo(() => {
    return publicAtas.filter((ata) => {
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matches =
          ata.numero.toLowerCase().includes(q) ||
          ata.nome.toLowerCase().includes(q) ||
          ata.orgao.toLowerCase().includes(q) ||
          ata.uasg.toLowerCase().includes(q) ||
          ata.objeto.toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (orgaoFilter && ata.orgao !== orgaoFilter) return false;
      if (uasgFilter.trim() && !ata.uasg.includes(uasgFilter.trim())) return false;
      if (estadoFilter && ata.estado !== estadoFilter) return false;
      if (esferaFilter !== 'todas' && ata.esfera !== esferaFilter) return false;
      if (selectedCategory !== null && !ata.categorias.includes(selectedCategory)) return false;
      if (statusFilter !== 'todas' && ata.status !== statusFilter) return false;
      return true;
    });
  }, [publicAtas, searchTerm, orgaoFilter, uasgFilter, estadoFilter, esferaFilter, selectedCategory, statusFilter]);

  const groupedByCategory = useMemo(() => {
    const groups: { categoria: { id: number; nome: string }; atas: typeof filteredAtas }[] = [];
    categorias.forEach((cat) => {
      const atasInCat = filteredAtas.filter((a) => a.categorias.includes(cat.id));
      if (atasInCat.length > 0) {
        groups.push({ categoria: cat, atas: atasInCat });
      }
    });
    const semCategoria = filteredAtas.filter(
      (a) => a.categorias.length === 0 || !a.categorias.some((c) => categorias.find((cat) => cat.id === c)),
    );
    if (semCategoria.length > 0) {
      groups.push({ categoria: { id: -1, nome: 'Sem categoria' }, atas: semCategoria });
    }
    return groups;
  }, [filteredAtas, categorias]);

  const hasActiveFilters =
    searchTerm.trim() || orgaoFilter || uasgFilter.trim() || estadoFilter || esferaFilter !== 'todas' || selectedCategory !== null || statusFilter !== 'todas';

  const clearFilters = () => {
    setSearchTerm('');
    setOrgaoFilter('');
    setUasgFilter('');
    setEstadoFilter('');
    setEsferaFilter('todas');
    setSelectedCategory(null);
    setStatusFilter('todas');
  };

  const formatDate = (date: string) =>
    date ? new Date(date + 'T00:00:00').toLocaleDateString('pt-BR') : '—';

  const getAttachmentInfo = (attachment: string) => {
    const [name, url] = attachment.split('::');
    return {
      name,
      url: url || `#download-${name}`,
      external: Boolean(url),
    };
  };

  const getEsferaBadgeClass = (esfera: string) => {
    if (esfera === 'Federal') return 'bg-blue-50 text-blue-600 border-blue-200';
    if (esfera === 'Estadual') return 'bg-green-50 text-green-600 border-green-200';
    if (esfera === 'Municipal') return 'bg-orange-50 text-orange-600 border-orange-200';
    return 'bg-gray-50 text-gray-500 border-gray-200';
  };

  const AtaCard = ({ ata }: { ata: Ata }) => {
    const isExpanded = expandedAta === ata.id;
    const cats = getCategoriaNomes(ata.categorias);
    return (
      <div className="bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow overflow-hidden">
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-xs font-bold text-donnos-green bg-donnos-green/10 px-2 py-0.5 rounded">
                  {ata.numero}
                </span>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                    ata.status === 'Ativa' ? 'bg-donnos-green/10 text-donnos-green border-donnos-green/20' : 'bg-gray-50 text-gray-500 border-gray-200'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${ata.status === 'Ativa' ? 'bg-donnos-green' : 'bg-gray-400'}`}></span>
                  {ata.status}
                </span>
                {ata.esfera && (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${getEsferaBadgeClass(ata.esfera)}`}>
                    {ata.esfera}
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold text-gray-900 leading-snug">{ata.nome}</h3>
            </div>
          </div>

          {/* Órgão + UASG + Estado */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 mb-3">
            <div className="flex items-center gap-1.5">
              <i className="ri-government-line text-gray-400 text-sm"></i>
              <span className="text-xs text-gray-600">{ata.orgao}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-hashtag text-gray-400 text-sm"></i>
              <span className="text-xs text-gray-600 font-mono">UASG: {ata.uasg}</span>
            </div>
            {ata.estado && (
              <div className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                <span className="text-xs text-gray-600 font-medium">{ata.estado}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-gray-400 text-sm"></i>
              <span className="text-xs text-gray-600">Edital: {formatDate(ata.dataEdital)}</span>
            </div>
          </div>

          {/* Objeto */}
          <p className={`text-sm text-gray-500 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
            {ata.objeto}
          </p>

          {/* Categorias */}
          {cats.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {cats.map((c) => (
                <span key={c} className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {c}
                </span>
              ))}
            </div>
          )}

          {/* Expanded details */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider block">Vigência início</span>
                  <span className="text-sm text-gray-700">{formatDate(ata.dataInicio)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider block">Vigência fim</span>
                  <span className="text-sm text-gray-700">{formatDate(ata.dataFim)}</span>
                </div>
              </div>
              {ata.anexos.length > 0 && (
                <div>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1.5">Documentos para download</span>
                  <div className="space-y-1.5">
                    {ata.anexos.map((anexo) => {
                      const attachment = getAttachmentInfo(anexo);
                      return (
                        <a
                          key={anexo}
                          href={attachment.url}
                          target={attachment.external ? '_blank' : undefined}
                          rel={attachment.external ? 'noreferrer' : undefined}
                          download={!attachment.external ? attachment.name : undefined}
                          className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 hover:bg-donnos-green/5 rounded-lg px-3 py-2 transition-colors group cursor-pointer"
                        >
                          <i className="ri-file-pdf-line text-red-400 group-hover:text-red-500"></i>
                          <span className="truncate flex-1">{attachment.name}</span>
                          <i className="ri-download-2-line text-gray-400 group-hover:text-donnos-green transition-colors flex-shrink-0"></i>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer actions */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
            <button
              onClick={() => setExpandedAta(isExpanded ? null : ata.id)}
              className="text-xs font-medium text-donnos-green hover:text-donnos-green-dark inline-flex items-center gap-1 cursor-pointer"
            >
              {isExpanded ? 'Ver menos' : 'Ver detalhes'}
              <i className={isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>
            <a
              href="#contato"
              className="text-xs font-semibold text-white bg-donnos-green hover:bg-donnos-green-dark px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors whitespace-nowrap"
            >
              <i className="ri-chat-quote-line"></i>
              Solicitar cotação
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-donnos-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-donnos-green/20 via-transparent to-transparent"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-donnos-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="relative w-full px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold text-donnos-green uppercase tracking-wider bg-donnos-green/10 px-3 py-1 rounded-full mb-4">
              Consulta de Atas
            </span>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Atas de Registro de Preços
            </h1>
            <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto mb-8">
              Consulte nossas atas de registro de preços por número, órgão, UASG, estado, esfera ou categoria. Encontre as melhores condições para sua instituição.
            </p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por número, nome, órgão, UASG ou objeto..."
                className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-4 py-3.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green focus:bg-white/15 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-24 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Filtros</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-donnos-green hover:text-donnos-green-dark font-medium cursor-pointer"
                  >
                    Limpar tudo
                  </button>
                )}
              </div>

              {/* Esfera */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Esfera</label>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setEsferaFilter('todas')}
                    className={`flex-1 text-xs font-medium px-2 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                      esferaFilter === 'todas'
                        ? 'bg-donnos-green text-white border-donnos-green'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-donnos-green/50'
                    }`}
                  >
                    Todas
                  </button>
                  {esferaOptions.map((e) => (
                    <button
                      key={e}
                      onClick={() => setEsferaFilter(e)}
                      className={`flex-1 text-xs font-medium px-2 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                        esferaFilter === e
                          ? 'bg-donnos-green text-white border-donnos-green'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-donnos-green/50'
                      }`}
                    >
                      {e === 'Federal' ? 'Fed.' : e === 'Estadual' ? 'Est.' : 'Mun.'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estado */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Estado (UF)</label>
                <select
                  value={estadoFilter}
                  onChange={(e) => setEstadoFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green bg-white cursor-pointer"
                >
                  <option value="">Todos os estados</option>
                  {estados.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>

              {/* Órgão */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Órgão</label>
                <select
                  value={orgaoFilter}
                  onChange={(e) => setOrgaoFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green bg-white cursor-pointer"
                >
                  <option value="">Todos os órgãos</option>
                  {orgaos.map((org) => (
                    <option key={org} value={org}>{org}</option>
                  ))}
                </select>
              </div>

              {/* UASG */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">UASG</label>
                <div className="relative">
                  <i className="ri-hashtag absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={uasgFilter}
                    onChange={(e) => setUasgFilter(e.target.value)}
                    placeholder="Ex: 150001"
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green font-mono"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <div className="flex gap-1.5">
                  {(['todas', 'Ativa', 'Encerrada'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`flex-1 text-xs font-medium px-2 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                        statusFilter === s
                          ? 'bg-donnos-green text-white border-donnos-green'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-donnos-green/50'
                      }`}
                    >
                      {s === 'todas' ? 'Todas' : s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categorias */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Categoria</label>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`w-full px-3 py-2 rounded-xl text-sm transition-all cursor-pointer border inline-flex items-center justify-between gap-2 ${
                        selectedCategory === null
                          ? 'bg-donnos-green text-white border-donnos-green shadow-sm font-semibold'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-donnos-green/50 hover:bg-donnos-green/5'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-stack-line text-sm"></i>
                        Todas as categorias
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        selectedCategory === null ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {publicAtas.length}
                      </span>
                    </button>
                  </li>
                  {categorias.map((cat) => {
                    const count = publicAtas.filter((a) => a.categorias.includes(cat.id)).length;
                    if (count === 0) return null;
                    return (
                      <li key={cat.id}>
                        <button
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`w-full px-3 py-2 rounded-xl text-sm transition-all cursor-pointer border inline-flex items-center justify-between gap-2 ${
                            selectedCategory === cat.id
                              ? 'bg-donnos-green text-white border-donnos-green shadow-sm font-semibold'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-donnos-green/50 hover:bg-donnos-green/5'
                          }`}
                        >
                          <span className="inline-flex items-center gap-2 min-w-0">
                            <i className="ri-folder-3-line text-sm flex-shrink-0"></i>
                            <span className="truncate">{cat.nome}</span>
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            selectedCategory === cat.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Toolbar: results + view mode */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <p className="text-sm text-gray-500">
                {filteredAtas.length === 0
                  ? 'Nenhuma ata encontrada'
                  : `${filteredAtas.length} ata${filteredAtas.length !== 1 ? 's' : ''} encontrada${filteredAtas.length !== 1 ? 's' : ''}`}
              </p>
              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1">
                <button
                  onClick={() => setViewMode('lista')}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer inline-flex items-center gap-1.5 ${
                    viewMode === 'lista' ? 'bg-donnos-green text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-list-unordered"></i>
                  Lista
                </button>
                <button
                  onClick={() => setViewMode('categorizado')}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer inline-flex items-center gap-1.5 ${
                    viewMode === 'categorizado' ? 'bg-donnos-green text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-stack-line"></i>
                  Por Categoria
                </button>
              </div>
            </div>

            {/* Empty state */}
            {filteredAtas.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Nenhuma ata encontrada</h3>
                <p className="text-sm text-gray-500 mb-4">Tente ajustar os filtros ou a busca.</p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-donnos-green font-medium hover:underline cursor-pointer"
                  >
                    Limpar todos os filtros
                  </button>
                )}
              </div>
            ) : viewMode === 'lista' ? (
              /* List View */
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {filteredAtas.map((ata) => (
                  <AtaCard key={ata.id} ata={ata} />
                ))}
              </div>
            ) : (
              /* Categorized View */
              <div className="space-y-5">
                {groupedByCategory.map((group) => (
                  <section key={group.categoria.id} className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100 bg-gray-50/80 px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-donnos-green/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-folder-3-line text-donnos-green text-lg"></i>
                        </div>
                        <div>
                          <h3 className="text-base font-extrabold text-gray-950">{group.categoria.nome}</h3>
                          <span className="text-xs text-gray-500">{group.atas.length} ata{group.atas.length !== 1 ? 's' : ''} vinculada{group.atas.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-donnos-green/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-donnos-green">
                        <i className="ri-price-tag-3-line"></i>
                        Por categoria
                      </span>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4">
                      {group.atas.map((ata) => (
                        <AtaCard key={ata.id} ata={ata} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
